import { BehaviorSubject, from, of, switchMap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { convertSvgToDataURL, encodeSVG } from '@et/utils';
import dayjs from 'dayjs';
import { STAMPS_BY_STATE, STAMP_TEMPLATES_PATH } from '@et/typings';
import { environment } from '@et/environment';
import { DomSanitizer } from '@angular/platform-browser';

export interface StampCreation {
  signingAgent: string;
  state: string;
  name: string;
  commissionNumber: string;
  county: string;
  utcExpiresOn: string;
  utcIssuedOn: string;
  roundedStamp: string;
  rectStamp: string;
  type?: 'png' | 'svg';
}

@Injectable({ providedIn: 'root' })
export class StampCreationService {
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);

  private _rectStamp$ = new BehaviorSubject<string | null>(null);
  private _circleStamp$ = new BehaviorSubject<string | null>(null);

  private stateStamps = STAMPS_BY_STATE;
  private stampTemplatePath = STAMP_TEMPLATES_PATH;

  rectStamp$ = this._rectStamp$.asObservable();
  circleStamp$ = this._circleStamp$.asObservable();

  set rectStamp(value: string | null) {
    this._rectStamp$.next(value);
  }

  /**
   * The function takes StampCreation value and makes stamp out of it
   * @param {StampCreation} value - StampCreation
   */
  updateStamps(value: StampCreation) {
    const stateStamps = this.getStampsImgPath(value.state);
    const type = value.type || ('png' as 'png' | 'svg');
    if (stateStamps?.['rect-1']) {
      const opt = { width: 1024, height: 408, type };
      this.makeStmpFromImg(value, stateStamps['rect-1'], opt).subscribe(
        (dataUrl) => this._rectStamp$.next(dataUrl),
      );
    } else {
      this._rectStamp$.next(null);
    }
    if (stateStamps?.['circle-1']) {
      const opt = { width: 1024, height: 1024, type };
      this.makeStmpFromImg(value, stateStamps['circle-1'], opt).subscribe(
        (dataUrl) => this._circleStamp$.next(dataUrl),
      );
    } else {
      this._circleStamp$.next(null);
    }
  }

  /**
   * The function `updateStampFromInput` takes a value and type as input, creates a stamp based on the
   * value, and updates the corresponding stamp based on the type.
   * @param {string} value - The `value` parameter is a string that represents the value to be used for
   * creating the stamp. It could be any value that is relevant to the stamp creation process.
   * @param {string} type - The "type" parameter in the "updateStampFromInput" function is a string that
   * represents the type of stamp being updated. It can have two possible values: "rect-1" or "circle-1".
   */
  updateStampFromInput(value: string, type: string) {
    this.makeStamp(
      value,
      {} as StampCreation,
      {
        type: 'svg',
      } as never,
    ).subscribe((dataUrl) => {
      if (type === 'rect-1') {
        this._rectStamp$.next(dataUrl);
      } else if (type === 'circle-1') {
        this._circleStamp$.next(dataUrl);
      }
    });
  }

  /**
   * The function takes an image url and returns Observable resolving to image dataURL
   * @param {String} stampImageUrl - string
   */
  getStampImage(stampImageUrl: string) {
    const reqUrl = environment.profilev2.domain + '/api/stamps/file';
    const params = new HttpParams().append('url', stampImageUrl);

    return this.http.get(reqUrl, { responseType: 'blob', params }).pipe(
      switchMap((blob) => {
        return blob.text().then((txt) => {
          if (txt.includes('<svg')) {
            return convertSvgToDataURL(txt);
          } else {
            const unsafeImg = URL.createObjectURL(blob);
            return this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
          }
        });
      }),
    );
  }

  /**
   * Loads an image, fits it into a fixed canvas (1024x408) preserving aspect ratio, centers it,
   * and returns a PNG data URL of the resulting image.
   *
   * The image is scaled using an "object-fit: contain" strategy so it is not distorted.
   * The scaled image is drawn centered on the canvas and the canvas is exported as a PNG data URL.
   *
   * @param {string} imageSrc - Source URL or data URL of the image to process.
   * @returns {Promise<string>} A promise that resolves to a PNG data URL of the processed image.
   * @throws {Error} If the image fails to load or a 2D canvas context cannot be obtained.
   */
  async modifyImage(imageSrc: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = imageSrc;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 1024;
        canvas.height = 408;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get 2D context from canvas'));
          return;
        }

        // Calculate scale to fit image without distorting aspect ratio (object-fit: contain)
        const scale = Math.min(
          canvas.width / img.width,
          canvas.height / img.height,
        );
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        const offsetX = (canvas.width - drawWidth) / 2;
        const offsetY = (canvas.height - drawHeight) / 2;

        // Draw the image centered on the canvas
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Convert to PNG data URL and resolve
        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = (err) => {
        reject(new Error('Failed to load image: ' + err));
      };
    });
  }

  /**
   * The function gets a stamp template and calls makeStamp function
   * @param {StampCreation} value - StampCreation
   * @param {String} imgPath - string
   * @param {{ width: number; height: number }} opt - Width and height of the stamp
   */
  private makeStmpFromImg(
    value: StampCreation,
    imgPath: string,
    opt?: { width: number; height: number; type: 'png' | 'svg' },
  ) {
    return this.http
      .get(imgPath, { responseType: 'text' })
      .pipe(switchMap((image) => this.makeStamp(image, value, opt)));
  }

  /**
   * The function takes svg image, replaces tags and returns observable
   * resolving to dataUrl
   * @param {String} image - String
   * @param {StampCreation} value - StampCreation
   * @param {{ width: number; height: number }} opt - Width and height of the stamp
   */
  makeStamp(
    image: string,
    value: StampCreation,
    opt?: { width: number; height: number; type: 'png' | 'svg' },
  ) {
    const expDate = this.formatDateByState(value.utcExpiresOn, value.state);
    const county = this.formatCountyByState(value.county, value.state);

    image = image.replace(
      new RegExp('{{NOTARY_NAME}}', 'g'),
      value?.name?.toUpperCase(),
    );
    image = image.replace(new RegExp('{{COUNTY}}', 'g'), county);
    image = image.replace(
      new RegExp('{{COMMISSION_NUMBER}}', 'g'),
      value.commissionNumber,
    );
    image = image.replace(new RegExp('{{COMMISSION_EXP}}', 'g'), expDate);
    if (opt?.type === 'svg') {
      return of(encodeSVG(image));
    } else {
      return from(convertSvgToDataURL(image, opt));
    }
  }

  /**
   * It takes county and state and returns formatted county name depending on state
   * @param county - string
   * @param state - string
   * @returns - string
   */
  private formatCountyByState(county: string, state: string) {
    switch (state) {
      case 'Georgia':
        return county;
      default:
        return county?.toUpperCase();
    }
  }

  /**
   * It takes date and state and returns formatted date depending on state
   * @param date - string
   * @param state - string
   * @returns - string
   */
  private formatDateByState(date: string, state: string) {
    switch (state) {
      case 'Texas':
        return this.formatDate(date, 'MMMM D, YYYY');
      default:
        return this.formatDate(date, 'MMM DD, YYYY').toUpperCase();
    }
  }

  /**
   * The function takes state name and returns stamp templates paths
   * for this state
   * @param {String} state - String
   */
  private getStampsImgPath(state: string):
    | {
        state: string;
        'rect-1'?: string;
        'circle-1'?: string;
      }
    | undefined {
    const path = this.stampTemplatePath;

    const stateStamps = this.stateStamps.find((s) => s.state === state);
    const stateStampsCp = { ...stateStamps } as {
      state: string;
      'rect-1'?: string;
      'circle-1'?: string;
    };

    if (stateStampsCp?.['rect-1']) {
      stateStampsCp['rect-1'] = path + stateStampsCp['rect-1'];
    }
    if (stateStampsCp?.['circle-1']) {
      stateStampsCp['circle-1'] = path + stateStampsCp['circle-1'];
    }

    return stateStampsCp;
  }

  /**
   * The function takes date string and format and
   * returns date in provided format
   * @param {String} date - String
   * @param {String} format - String
   */
  private formatDate(date: string, format: string) {
    if (dayjs(date).isValid()) {
      return dayjs(new Date(date)).format(format);
    }
    return '';
  }
}
